import { memo, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'

import { EventRecording } from '@crew/models/domain'
import { useLazyGetEventRecordingUrlQuery } from '@crew/apis/project/projectApis'

import { CrewMeetingArchiveItem } from './components/crewMeetingArchiveItem/crewMeetingArchiveItem'
import { CrewMeetingArchiveFileUploader } from './components/crewMeetingArchiveFileUploader/crewMeetingArchiveFileUploader'

import KeyboardArrowDown from '~icons/material-symbols/keyboard-arrow-down'
import KeyboardArrowUp from '~icons/material-symbols/keyboard-arrow-up'
import { FileExtensions } from '@crew/enums/dist/app'
import { CrewPlayer } from '../crewPlayer/crewPlayer'
import {
  PreviewSupportedAudioMimeType,
  PreviewSupportedVideoMimeType,
} from '@crew/utils/file'
import { assertNever } from '@crew/utils'

export type CrewMeetingArchiveProps = {
  eventId: string
  archiveFiles: EventRecording[]
  showDeleteButton: boolean
  showDownloadButton: boolean
}

export const CrewMeetingArchive = memo((props: CrewMeetingArchiveProps) => {
  // CrewVideoPlayer制御用のstate
  const [isOpen, setIsOpen] = useState(false)
  const [eventRecordingsShowAll, setEventRecordingsShowAll] =
    useState<boolean>(true)
  const [selectedFileMimeType, setSelectedFileMimeType] = useState<
    PreviewSupportedVideoMimeType | PreviewSupportedAudioMimeType
  >(PreviewSupportedVideoMimeType.Mp4)

  // Base style for collapsed animation of archive frame
  const archiveBaseClassNames = useMemo(() => {
    return 'transition-all duration-500 ease-linear'
  }, [])

  const [lazyGetEventRecordingUrlQuery, lazyGetEventRecordingUrlQueryResult] =
    useLazyGetEventRecordingUrlQuery()

  // ファイルのリンクを押したときに、再生したいファイルのUrlを取得する
  const handleLinkClick = useCallback(
    (recordingId: string, fileName: string | null) => {
      lazyGetEventRecordingUrlQuery({
        eventRecordingId: recordingId,
      })

      // ファイル名の拡張子からmimeTypeを判定してセット
      if (fileName) {
        const extension = fileName.split('.').pop()?.toLowerCase()
        switch (extension) {
          case FileExtensions.Mp4:
            setSelectedFileMimeType(PreviewSupportedVideoMimeType.Mp4)
            break
          case FileExtensions.Mp3:
            setSelectedFileMimeType(PreviewSupportedAudioMimeType.Mp3)
            break
          case FileExtensions.Wav:
            setSelectedFileMimeType(PreviewSupportedAudioMimeType.Wav)
            break
          case FileExtensions.Oga:
            setSelectedFileMimeType(PreviewSupportedAudioMimeType.Oga)
            break
          case FileExtensions.M4a:
            setSelectedFileMimeType(PreviewSupportedAudioMimeType.M4a)
            break
          default:
            // 不明な場合はエラー
            assertNever(
              extension as never,
              `Unsupported file extension: ${extension}`
            )
        }
      } else {
        // ファイル名がない場合はmp4とする
        setSelectedFileMimeType(PreviewSupportedVideoMimeType.Mp4)
      }

      setIsOpen(true)
    },
    [lazyGetEventRecordingUrlQuery]
  )

  // CrewVideoPlayerを閉じる
  const handleLightBoxClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Handle button collapse or show all recording list
  const handleShowAllRecordingsButtonClick = useCallback(() => {
    setEventRecordingsShowAll(!eventRecordingsShowAll)
  }, [eventRecordingsShowAll])

  return (
    <div className="inline-flex flex-row gap-1 relative">
      {/* 録画ファイル一覧 */}
      <div
        className={classNames(
          archiveBaseClassNames,
          !eventRecordingsShowAll || props.archiveFiles.length === 0
            ? 'max-h-6 overflow-hidden'
            : null
        )}
      >
        {props.archiveFiles.map((archiveFile) => (
          <CrewMeetingArchiveItem
            key={archiveFile.id}
            recordingInfo={archiveFile}
            showDeleteButton={props.showDeleteButton}
            showDownloadButton={props.showDownloadButton}
            onLinkClick={() => {
              handleLinkClick(archiveFile.id, archiveFile.fileName)
            }}
          />
        ))}
        {lazyGetEventRecordingUrlQueryResult.data?.url &&
          selectedFileMimeType && (
            <CrewPlayer
              streamingUrl={lazyGetEventRecordingUrlQueryResult.data.url}
              isOpen={isOpen}
              showDownloadButton={props.showDownloadButton}
              onClose={handleLightBoxClose}
              mimeType={selectedFileMimeType}
            />
          )}
        <CrewMeetingArchiveFileUploader eventId={props.eventId} />
      </div>

      {/* 録画ファイル一覧の折り畳み表示ボタン */}
      {props.archiveFiles.length > 0 && (
        <div
          onClick={handleShowAllRecordingsButtonClick}
          className="cursor-pointer"
        >
          {eventRecordingsShowAll ? (
            <KeyboardArrowUp width={24} height={24} />
          ) : (
            <KeyboardArrowDown width={24} height={24} />
          )}
        </div>
      )}
    </div>
  )
})
